/**
 * Reporting a completed purchase to Google.
 *
 * ── Which of Google's two models this is ────────────────────────────────────
 *
 * Google Ads measures a web conversion in one of two ways, and its setup
 * wizard picks for you:
 *
 *   EVENT-BASED   the conversion action listens for a named event, and the
 *                 snippet it gives you is
 *                   gtag('event', 'purchase', { ... })
 *                 There is no conversion label in this model, which is why
 *                 hunting for one in the Google Ads UI turns up nothing.
 *
 *   CLASSIC       the conversion action has its own label, and the snippet is
 *                   gtag('event', 'conversion', { send_to: 'AW-123/AbC-def' })
 *
 * We fire the event-based one unconditionally, because `purchase` is also the
 * standard GA4 ecommerce event: the same call gives the GA4 property real
 * revenue data it has never had, and Google Ads reads it either through a
 * linked GA4 property or through the Google tag on the page. If a classic
 * conversion label is ever configured as well, that is sent too — the two are
 * separate conversion actions and do not double-count each other.
 *
 * ── Why this is not fired where Google suggested ────────────────────────────
 *
 * The wizard offers "Page load" or "Click". Neither fits. Page load wants a
 * confirmation URL, and only one of our two payment flows has one:
 * /account/pay/[id] redirects to /payment/success, while pay-by-link
 * (/pay/[token]) confirms IN PLACE with no navigation — and that is the flow
 * most customers use, because it needs no account. Click fires before the card
 * is charged, so it would count declines as sales. Both call sites here fire
 * at the moment the charge actually succeeds.
 *
 * ── The ids are public ──────────────────────────────────────────────────────
 *
 * GOOGLE_ADS_ID ships in the page source for anyone to read, exactly like the
 * Meta Pixel id in meta-config.ts. It is only needed for the classic path, or
 * to route events to Google Ads without linking GA4.
 */

/** AW-XXXXXXXXXX. Optional: unset is fine when GA4 is linked to Google Ads. */
export const GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID ?? "";

/**
 * Only for the CLASSIC model. Leave empty on the event-based one — there is
 * no label to set, and an empty label must never produce a half-formed
 * `send_to` like "AW-123/".
 */
export const GOOGLE_ADS_PURCHASE_LABEL =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_PURCHASE_LABEL ?? "";

export function googleAdsConfigured(): boolean {
  return GOOGLE_ADS_ID.startsWith("AW-");
}

/**
 * Attach the customer's own details to the next conversion.
 *
 * This is Enhanced Conversions, and for this business it is the difference
 * between measuring most of the leads and measuring a fraction of them. A
 * browser cookie is the weakest link in attribution — blocked, cleared,
 * expired, or a visitor who clicked the ad on their phone and filled the form
 * in on a laptop. An email address survives all of that, and the customer
 * types one into every quote form we have.
 *
 * gtag hashes it in the browser before anything leaves the page; the raw
 * address is never sent to Google. Phone numbers need E.164 to be usable, so
 * only a number we can confidently normalise is passed.
 */
export function setConversionUserData(opts: { email?: string | null; phone?: string | null }): void {
  if (typeof window === "undefined" || !window.gtag) return;
  const email = opts.email?.trim().toLowerCase();
  const phone = normalizeE164(opts.phone);
  if (!email && !phone) return;

  window.gtag("set", "user_data", {
    ...(email ? { email } : {}),
    ...(phone ? { phone_number: phone } : {}),
  });
}

/**
 * A US phone number as E.164, or null.
 *
 * Google discards anything else, and a wrong number is worse than none: it
 * cannot match, and it is still personal data we sent for no reason. Ten
 * digits become +1; eleven starting with 1 become +1; anything else — an
 * extension, an international number, a half-typed field — is left out.
 */
export function normalizeE164(phone?: string | null): string | null {
  const digits = (phone ?? "").replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

/**
 * Report a quote request.
 *
 * This is the conversion that matters for this business, and it was missing
 * entirely: the ad sends people to the site to ASK FOR A QUOTE. They do not
 * pay on the spot — the shop prices the job, then sends a pay link days
 * later. Purchases are rare and late; quote requests are the thing an ad
 * actually produces.
 *
 * Without this, Google Ads had one conversion action (Purchase) that almost
 * never fires, so Smart Bidding had nothing to learn from and the campaign
 * looked like it was producing nothing.
 *
 * `generate_lead` is GA4's standard name for it, which is what makes it
 * importable into Google Ads as a conversion action without any new tag.
 *
 * No value is sent. A quote request is not worth a number we know yet, and
 * inventing one would teach the bidding algorithm something false. Set a
 * per-conversion value in Google Ads instead, where it can be changed without
 * a deploy.
 */
export function trackLead(opts: {
  category?: string;
  quoteId?: string;
  /** For Enhanced Conversions — see setConversionUserData. */
  email?: string | null;
  phone?: string | null;
}): void {
  if (typeof window === "undefined" || !window.gtag) return;
  setConversionUserData({ email: opts.email, phone: opts.phone });
  window.gtag("event", "generate_lead", {
    ...(opts.category ? { lead_source: opts.category } : {}),
    // Dedup key when we have one, so a resubmitted form is still one lead.
    ...(opts.quoteId ? { transaction_id: opts.quoteId } : {}),
  });
}

/**
 * Report a completed purchase.
 *
 * `transactionId` is the PAYARC charge id, and it is what makes this safe to
 * call more than once: both Google Ads and GA4 drop a repeat of the same
 * transaction_id. The success page re-verifies on every mount, so a customer
 * refreshing their receipt would otherwise be a second sale.
 *
 * Amounts are dollars, not cents — gtag reads `value` as currency units, and
 * sending cents would report a $250 order as $25,000.
 *
 * Consent is not bypassed. This goes through the page's own gtag, so it
 * inherits Consent Mode v2: a visitor who rejected cookies produces a
 * cookieless ping that Google models rather than attributes, which is the
 * correct behaviour.
 */
export function trackPurchase(opts: {
  valueDollars: number;
  transactionId: string;
  currency?: string;
  /** For Enhanced Conversions — see setConversionUserData. */
  email?: string | null;
}): void {
  if (typeof window === "undefined" || !window.gtag) return;
  setConversionUserData({ email: opts.email });
  const currency = opts.currency ?? "USD";

  // The event-based conversion, and GA4's ecommerce `purchase` at the same
  // time. Sent whatever else is configured.
  window.gtag("event", "purchase", {
    transaction_id: opts.transactionId,
    value: opts.valueDollars,
    currency,
  });

  // The classic conversion action, when one exists. Both halves guarded:
  // a label without an id, or an id without a label, would send a malformed
  // send_to that Google silently discards.
  if (googleAdsConfigured() && GOOGLE_ADS_PURCHASE_LABEL) {
    window.gtag("event", "conversion", {
      send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_PURCHASE_LABEL}`,
      transaction_id: opts.transactionId,
      value: opts.valueDollars,
      currency,
    });
  }
}
